import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  ChevronDown, Globe, Lock, Menu, X, ExternalLink,
  ShoppingBag, Search, Truck, Package,
  TrendingUp, Megaphone, Shield, Globe2, Store,
  Warehouse, BarChart3, Palette,
  GraduationCap, BookOpen, Users, Video, HelpCircle,
  CreditCard, Percent, DollarSign, Receipt,
  ListChecks, Tags, Gift, BadgeCheck, Share2,
  Sparkles, LayoutDashboard, Zap, Star,
  type LucideIcon,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { translations } from "@/i18n/translations";
import { Link, useNavigate } from "react-router-dom";
import logoEn from "@/assets/raneen-logo-en.png";
import logoAr from "@/assets/raneen-logo-ar.png";
import sellerAlaaAward from "@/assets/seller-alaa-award.jpg";

type MegaMenuItem = { label: string; desc: string; path: string; badge?: string };
type MegaMenuColumn = { title: string; items: MegaMenuItem[] };
type MegaMenuData = { left: MegaMenuColumn; right: MegaMenuColumn };

// Icon mapping per menu key, per column, per item index
const menuIcons: Record<string, { left: LucideIcon[]; right: LucideIcon[] }> = {
  start: {
    left: [ShoppingBag, ListChecks, Tags, Truck, Megaphone],
    right: [BookOpen, Shield, Sparkles, Package, Gift],
  },
  grow: {
    left: [TrendingUp, Megaphone, Shield, Sparkles, Globe2],
    right: [Store, Gift, LayoutDashboard],
  },
  services: {
    left: [Package, Truck, Share2, Warehouse],
    right: [Shield, Sparkles, Store],
  },
  resources: {
    left: [GraduationCap, Video, Search, Users],
    right: [Star, HelpCircle, BookOpen],
  },
  pricing: {
    left: [CreditCard, Percent, DollarSign],
    right: [Receipt, BarChart3],
  },
};

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t, switchLanguage, localePath, lang } = useLanguage();
  const s = t(translations.navbar);
  const mega = t(translations.megaMenu) as Record<string, MegaMenuData>;
  const navigate = useNavigate();

  const megaMenus = [
    { key: "start", label: s.start },
    { key: "grow", label: s.grow },
    { key: "services", label: s.services },
    { key: "resources", label: s.resources },
    { key: "pricing", label: s.pricing },
  ];

  const handleMouseEnter = (key: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(key);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 200);
  };

  const handleLinkClick = (path: string) => {
    setActiveMenu(null);
    setMobileOpen(false);
    if (path.includes("#")) {
      const [basePath, hash] = path.split("#");
      navigate(localePath(basePath));
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      navigate(localePath(path));
    }
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveMenu(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      <nav className="bg-background border-b border-border sticky top-0 z-50">
        <div className="container mx-auto flex items-center justify-between h-14 md:h-16 px-4">
          <Link to={localePath("")} className="flex items-center">
            <img src={lang === "en" ? logoEn : logoAr} alt="raneen.com" className={lang === "en" ? "h-10 md:h-[3.15rem]" : "h-28 md:h-[12.6rem]"} />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-0">
            {megaMenus.map((menu) => (
              <div
                key={menu.key}
                className="relative"
                onMouseEnter={() => handleMouseEnter(menu.key)}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  className={`text-sm font-semibold px-4 py-5 transition-colors flex items-center gap-1.5 ${
                    activeMenu === menu.key ? "text-primary" : "text-foreground hover:text-primary"
                  }`}
                  onClick={() => handleLinkClick(mega[menu.key].left.items[0].path)}
                >
                  {menu.label}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${activeMenu === menu.key ? "rotate-180" : ""}`} />
                </button>
                {activeMenu === menu.key && (
                  <div className="absolute bottom-0 start-4 end-4 h-[3px] bg-primary rounded-t-full" />
                )}
              </div>
            ))}
            <Button
              size="sm"
              asChild
              className="rounded-full px-6 font-bold ms-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Link to={localePath("/partners-program")}>
                {lang === "ar" ? "برنامج الشركاء" : "Partners Program"}
              </Link>
            </Button>
          </div>

          {/* Right actions */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={switchLanguage}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-md hover:bg-accent/50"
            >
              <Globe className="w-4 h-4" /> {s.language}
            </button>
            <Link to={localePath("/login")} className="flex items-center gap-1.5 text-sm font-medium border border-border rounded-full px-5 py-2 hover:bg-accent/50 transition-colors">
              <Lock className="w-4 h-4" /> {s.login}
            </Link>
            <Button size="sm" className="rounded-full px-6 font-bold ms-1" asChild>
              <Link to={localePath("/sell/register")}>{s.register}</Link>
            </Button>
          </div>

          <button
            className="md:hidden p-2 -mr-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Full-width mega menu */}
      {activeMenu && (
        <div
          className="hidden md:block fixed top-14 md:top-16 inset-x-0 z-40 bg-background border-b border-border shadow-lg"
          onMouseEnter={() => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }}
          onMouseLeave={handleMouseLeave}
        >
          {megaMenus.map((menu) => {
            if (activeMenu !== menu.key) return null;
            const data = mega[menu.key];
            const icons = menuIcons[menu.key];
            return (
              <div key={menu.key}>
                <div className="container mx-auto px-4 pt-20 pb-8">
                  <div className="grid grid-cols-[1fr_1fr_280px] gap-x-12">
                    {/* Left column */}
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-5">{data.left.title}</h3>
                      <div className="space-y-1">
                        {data.left.items.map((item, idx) => {
                          const Icon = icons.left[idx] || ShoppingBag;
                          return (
                            <MegaMenuItem key={item.path} item={item} Icon={Icon} onClick={handleLinkClick} />
                          );
                        })}
                      </div>
                    </div>
                    {/* Right column */}
                    <div>
                      <h3 className="text-lg font-bold text-foreground mb-5">{data.right.title}</h3>
                      <div className="space-y-1">
                        {data.right.items.map((item, idx) => {
                          const Icon = icons.right[idx] || ShoppingBag;
                          return (
                            <MegaMenuItem key={item.path} item={item} Icon={Icon} onClick={handleLinkClick} />
                          );
                        })}
                      </div>
                    </div>
                    {/* Promo card */}
                    <div className="bg-muted/50 rounded-lg p-5 flex flex-col justify-between border border-border/50">
                      <div>
                        <div className="w-full h-28 rounded-md mb-4 overflow-hidden relative">
                          <img
                            src={sellerAlaaAward}
                            alt={lang === "ar" ? "دكان علاء" : "Dukan Alaa"}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                          <div className="absolute bottom-2 start-2 flex items-center gap-1.5 text-white">
                            <Video className="w-3.5 h-3.5" strokeWidth={2} />
                            <span className="text-[11px] font-semibold">
                              {lang === "ar" ? "دكان علاء" : "Dukan Alaa"}
                            </span>
                          </div>
                        </div>
                        <h4 className="font-bold text-foreground mb-2 text-sm">
                          {lang === "ar" ? "اتفرج على قصص البائعين" : "Watch Sellers' Stories"}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                          {lang === "ar"
                            ? "شوف إزاي دكان علاء حقق نمو 97% مع رنين، واكتشف قصص نجاح تانية."
                            : "See how Dukan Alaa grew 97% with raneen, and discover more success stories."}
                        </p>
                      </div>
                      <button
                        onClick={() => handleLinkClick("/learn/seller-stories")}
                        className="w-full text-center text-xs font-semibold text-primary-foreground bg-primary hover:bg-primary/90 rounded-full py-2 transition-colors"
                      >
                        {lang === "ar" ? "اتفرج دلوقتي" : "Watch now"}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Bottom bar */}
                <div className="border-t border-border bg-muted/30">
                  <div className="container mx-auto px-4 py-3 flex items-center gap-8">
                    <button
                      onClick={() => handleLinkClick(data.left.items[0].path)}
                      className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {lang === "ar" ? "اكتشف مميزات البيع" : "Explore selling features"}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleLinkClick("/learn/faq")}
                      className="flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors"
                    >
                      {lang === "ar" ? "الأسئلة الشائعة" : "Review our FAQ"}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 top-14 bg-background z-40 overflow-y-auto">
          <div className="px-5 pt-6 pb-8 space-y-2">
            {megaMenus.map((menu) => (
              <MobileMenuSection
                key={menu.key}
                menuKey={menu.key}
                label={menu.label}
                data={mega[menu.key]}
                onLinkClick={handleLinkClick}
              />
            ))}
            <div className="py-3">
              <Button
                size="lg"
                asChild
                className="w-full rounded-full font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Link
                  to={localePath("/partners-program")}
                  onClick={() => setMobileOpen(false)}
                >
                  {lang === "ar" ? "برنامج الشركاء" : "Partners Program"}
                </Link>
              </Button>
            </div>
            <hr className="border-border my-4" />
            <div className="flex items-center gap-3 py-3">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <button onClick={() => { switchLanguage(); setMobileOpen(false); }} className="text-sm font-medium text-foreground">{s.language}</button>
            </div>
            <Link to={localePath("/login")} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-3">
              <Lock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">{s.login}</span>
            </Link>
            <div className="pt-3 pb-6">
              <Button size="lg" className="w-full rounded-full font-bold" asChild>
                <Link to={localePath("/sell/register")} onClick={() => setMobileOpen(false)}>{s.register}</Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MegaMenuItem = ({
  item,
  Icon,
  onClick,
}: {
  item: MegaMenuItem;
  Icon: LucideIcon;
  onClick: (path: string) => void;
}) => (
  <button
    onClick={() => onClick(item.path)}
    className="w-full text-start flex items-start gap-4 py-3 px-2 rounded-lg hover:bg-accent/50 transition-colors group"
  >
    <Icon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
    <div className="min-w-0">
      <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors flex items-center gap-2">
        {item.badge && (
          <span className="text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">{item.badge}</span>
        )}
        {item.label}
      </p>
      <p className="text-sm text-muted-foreground mt-0.5">{item.desc}</p>
    </div>
  </button>
);

const MobileMenuSection = ({
  menuKey,
  label,
  data,
  onLinkClick,
}: {
  menuKey: string;
  label: string;
  data: MegaMenuData;
  onLinkClick: (path: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const icons = menuIcons[menuKey];
  const allItems = [...data.left.items, ...data.right.items];

  return (
    <div className="border-b border-border/50">
      <button
        className="flex items-center justify-between w-full text-base font-bold text-foreground py-5"
        onClick={() => setOpen(!open)}
      >
        {label}
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={open ? "pb-3 origin-top will-change-[transform,opacity] animate-in fade-in slide-in-from-top-1 duration-200" : "hidden"}>
        {/* Left section */}
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-3 mb-2">{data.left.title}</p>
        <div className="space-y-0.5 mb-4">
          {data.left.items.map((item, idx) => {
            const Icon = icons.left[idx] || ShoppingBag;
            return (
              <button
                key={item.path}
                onClick={() => onLinkClick(item.path)}
                className="flex items-start gap-3 w-full text-start py-3 px-3 rounded-lg active:bg-accent"
              >
                <Icon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-medium text-foreground flex items-center gap-2">
                    {item.badge && (
                      <span className="text-[10px] font-bold bg-primary text-primary-foreground px-2 py-0.5 rounded-full">{item.badge}</span>
                    )}
                    {item.label}
                  </p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
        {/* Right section */}
        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-3 mb-2">{data.right.title}</p>
        <div className="space-y-0.5">
          {data.right.items.map((item, idx) => {
            const Icon = icons.right[idx] || ShoppingBag;
            return (
              <button
                key={item.path}
                onClick={() => onLinkClick(item.path)}
                className="flex items-start gap-3 w-full text-start py-3 px-3 rounded-lg active:bg-accent"
              >
                <Icon className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Navbar;